// FancoilTable.jsx
import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Form,
  Modal,
  Carousel,
  OverlayTrigger,
  Popover,
  Badge,
  Tooltip
} from 'react-bootstrap';
import './FancoilTable.css';
import heic2any from 'heic2any';
import { getProjectNotes, getProjectPhotos, getProjectStatus } from '../../projectView';

function FancoilTable({
  hvacType,
  pageData,
  sortedData,
  startIndex,
  endIndex,
  currentPage,
  totalPages,
  rowsPerPage,
  sortColumn,
  sortDirection,
  handleSort,
  handlePrevPage,
  handleNextPage,
  handleRowsPerPageChange,
  handleOpenCarousel,
  showModal,
  carouselPhotos,
  carouselIndex,
  handleCloseModal,
  handleSelectCarousel,
  isVideoUrl,
  buildingId,
  onNotifyResident,
  onUpdateStatus, // callback for saving status to server
  dataFormat = "old", // Track data format: "old" or "new"
  projectMode = false,
}) {
  // Track which row is active for the status popover using a unique row ID.
  const [activeRowId, setActiveRowId] = useState(null);

  // Add state to track valid media files
  const [validMedia, setValidMedia] = useState({});

  // Add state to track which images are currently loaded (for lazy loading)
  const [loadedImages, setLoadedImages] = useState(new Set());

  // Add state to track photo-to-issue mapping for new format
  const [photoToIssueMap, setPhotoToIssueMap] = useState({});
  const [projectCarouselLabels, setProjectCarouselLabels] = useState([]);

  // HEIC support: cache converted URLs and track errors/in-flight conversions
  const [heicCache, setHeicCache] = useState({});
  const [heicErrors, setHeicErrors] = useState({});
  // Tracks per-URL whether the browser's NATIVE HEIC render failed.
  // Lets Safari (which decodes HEIC natively) work without waiting for heic2any,
  // and lets Chrome/Firefox fall back to the converted blob.
  const [heicNativeFailed, setHeicNativeFailed] = useState({});
  const heicInFlightRef = React.useRef(new Set());
  const heicObjectUrlsRef = React.useRef([]);
  const HEIC_STORAGE_PREFIX = 'heic-cache:';
  const HEIC_STORAGE_LIMIT = 1500000; // ~1.5MB per entry to avoid bloating localStorage

  // Clear loaded images when pageData changes (user navigates to different page)
  React.useEffect(() => {
    setLoadedImages(new Set());
  }, [pageData]);

  // Helper to check if an image should be loaded (only for currently visible rows)
  const shouldLoadImage = (imageUrl) => {
    if (!imageUrl) return false;
    
    // Check if this image is from a currently visible row
    const isVisible = pageData.some(row => {
      if (projectMode) {
        const { before, after } = getProjectPhotos(row);
        return before.includes(imageUrl) || after.includes(imageUrl);
      }
      if (hvacType === 'dryer-vent') {
        // For new format dryer vents, check photos field or before/after photos based on status
        if (dataFormat === "new") {
          const isOK = row.inspection === "OK" || row.inspection === "Resolved";
          if (isOK && row.rawData) {
            // For OK status, check before/after photos
            let allPhotos = [];
            Object.keys(row.rawData).forEach(key => {
              const keyLower = key.toLowerCase();
              if (keyLower.includes("model_sticker") || keyLower.includes("sticker_plate")) {
                return;
              }
              if (key.startsWith("before_photo_of") || key.startsWith("after_photo_of")) {
                const value = row.rawData[key];
                if (Array.isArray(value)) {
                  allPhotos.push(...value.filter(url => url && typeof url === "string"));
                } else if (value && typeof value === "string") {
                  allPhotos.push(value);
                }
              }
            });
            return allPhotos.includes(imageUrl);
          } else {
            // For Needs Service, check issue photos
            return row.photos && row.photos.includes(imageUrl);
          }
        } else {
          return (row.beforePhotos && row.beforePhotos.includes(imageUrl)) ||
                 (row.afterPhotos && row.afterPhotos.includes(imageUrl));
        }
      } else {
        return row.photos && row.photos.includes(imageUrl);
      }
    });
    
    return isVisible;
  };

  // Helper to mark an image as loaded
  const markImageLoaded = (imageUrl) => {
    setLoadedImages(prev => new Set([...prev, imageUrl]));
  };

  // Identify HEIC/HEIF files by extension
  const isHeicFile = (url = '') => {
    const lower = url.split('?')[0].toLowerCase();
    return lower.endsWith('.heic') || lower.endsWith('.heif');
  };

  // LocalStorage helpers for HEIC cache (persist across reloads)
  const readHeicFromStorage = (url) => {
    try {
      const stored = localStorage.getItem(`${HEIC_STORAGE_PREFIX}${url}`);
      return stored || null;
    } catch (err) {
      console.warn('HEIC storage read failed', err);
      return null;
    }
  };

  const writeHeicToStorage = (url, dataUrl) => {
    if (!dataUrl || dataUrl.length > HEIC_STORAGE_LIMIT) return;
    try {
      localStorage.setItem(`${HEIC_STORAGE_PREFIX}${url}`, dataUrl);
    } catch (err) {
      console.warn('HEIC storage write skipped', err);
    }
  };

  // Revoke generated object URLs on unmount
  React.useEffect(() => {
    return () => {
      heicObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  // Return a display-ready URL (converted for HEIC files)
  const getDisplayUrl = (url) => {
    if (isHeicFile(url)) {
      return heicCache[url] || null;
    }
    return url;
  };

  // Gather all photo URLs for a row (used for HEIC pre-processing)
  const collectPhotosForRow = (row) => {
    if (projectMode) {
      const { before, after } = getProjectPhotos(row);
      return [...before, ...after];
    }
    if (hvacType === 'dryer-vent') {
      if (dataFormat === "new") {
        const isOK = row.inspection === "OK" || row.inspection === "Resolved";
        if (isOK && row.rawData) {
          const before = [];
          const after = [];
          Object.keys(row.rawData).forEach((key) => {
            const keyLower = key.toLowerCase();
            if (keyLower.includes("model_sticker") || keyLower.includes("sticker_plate")) {
              return;
            }
            if (key.startsWith("before_photo_of")) {
              const value = row.rawData[key];
              if (Array.isArray(value)) {
                before.push(...value.filter((url) => url && typeof url === "string"));
              } else if (value && typeof value === "string") {
                before.push(value);
              }
            } else if (key.startsWith("after_photo_of")) {
              const value = row.rawData[key];
              if (Array.isArray(value)) {
                after.push(...value.filter((url) => url && typeof url === "string"));
              } else if (value && typeof value === "string") {
                after.push(value);
              }
            }
          });
          return [...before, ...after];
        }
        return row.photos || [];
      }
      return [...(row.beforePhotos || []), ...(row.afterPhotos || [])];
    }
    return row.photos || [];
  };

  const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  // Convert HEIC/HEIF images to JPEG blob URLs for browser display
  const convertHeicToJpeg = async (url) => {
    try {
      // Check persisted cache first
      const stored = readHeicFromStorage(url);
      if (stored) {
        setHeicCache((prev) => ({ ...prev, [url]: stored }));
        setValidMedia((prev) => ({ ...prev, [url]: true }));
        heicInFlightRef.current.delete(url);
        return;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch HEIC asset: ${response.status}`);
      const blob = await response.blob();
      const converted = await heic2any({ blob, toType: "image/jpeg", quality: 0.8 });
      const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
      const objectUrl = URL.createObjectURL(jpegBlob);
      heicObjectUrlsRef.current.push(objectUrl);
      setHeicCache((prev) => ({ ...prev, [url]: objectUrl }));
      setValidMedia((prev) => ({ ...prev, [url]: true }));

      // Persist small conversions to localStorage to avoid redoing work on reloads
      blobToDataUrl(jpegBlob)
        .then((dataUrl) => writeHeicToStorage(url, dataUrl))
        .catch((err) => console.warn('HEIC persist skipped', err));
    } catch (err) {
      // Don't mark as invalid yet — let the browser try a native render as a fallback
      // (Safari can decode HEIC natively even when heic2any fails on certain files)
      console.warn('HEIC conversion failed, will try native render fallback for', url, err);
      setHeicErrors((prev) => ({ ...prev, [url]: true }));
    } finally {
      heicInFlightRef.current.delete(url);
    }
  };

  // Pre-convert HEIC files for visible rows so thumbnails render
  React.useEffect(() => {
    const heicUrls = new Set();
    pageData.forEach((row) => {
      collectPhotosForRow(row).forEach((url) => {
        if (isHeicFile(url)) {
          heicUrls.add(url);
        }
      });
    });

    heicUrls.forEach((url) => {
      if (heicCache[url] || heicErrors[url] || heicInFlightRef.current.has(url)) return;
      heicInFlightRef.current.add(url);
      convertHeicToJpeg(url);
    });
  }, [pageData, dataFormat, hvacType, projectMode, heicCache, heicErrors]);

  // Ensure HEIC files inside the carousel are converted as well
  React.useEffect(() => {
    (carouselPhotos || []).forEach((url) => {
      if (!isHeicFile(url)) return;
      if (heicCache[url] || heicErrors[url] || heicInFlightRef.current.has(url)) return;
      heicInFlightRef.current.add(url);
      convertHeicToJpeg(url);
    });
  }, [carouselPhotos, heicCache, heicErrors]);

  // Determine the HVAC column heading based on type.
  const hvacHeading = hvacType === 'heatpump' ? 'Heatpump' : hvacType === 'dryer-vent' ? 'Vent' : 'Fancoil';

  // Helper to return a sort arrow for a given column.
  const getSortArrow = (columnKey) => {
    if (sortColumn === columnKey) {
      return sortDirection === 'asc' ? ' ↑' : ' ↓';
    }
    return '';
  };

  // Helper to return the appropriate badge CSS class for a given status.
  function getBadgeClass(status) {
    if (!status) return '';
    if (status === 'OK') return 'badge-ok';
    if (status === 'Needs Service') return 'badge-needs-service';
    if (status === 'Resolved') return 'badge-resolved';
    if (status === 'Missed') return 'badge-missed';
    return '';
  }

  // Visible label inside the badge — internal status uses 'Missed' for filtering,
  // but the column shows "No Access" to match the resident-facing terminology.
  function getBadgeLabel(status) {
    if (status === 'Missed') return 'No Access';
    return status || '-';
  }

  // Helper to generate a unique ID for each row.
  function getRowId(row, idx) {
    return row.objectId || `${row.unit}-${idx}`;
  }

  // Called when a new status is selected from the popover.
  async function handleSelectStatus(row, newStatus) {
    try {
      if (onUpdateStatus) {
        await onUpdateStatus(row, newStatus);
      }
      // Close the popover by clearing the active row.
      setActiveRowId(null);
    } catch (err) {
      console.error('Error saving status:', err);
      alert('Could not update status. Please try again.');
    }
  }

  // Build a custom popover for a given row.
  function renderStatusPopover(row) {
    const rowUnit = row.unit || 'N/A';
    return (
      <Popover id={`popover-status-${getRowId(row)}`}>
        <Popover.Body style={{ padding: 0 }}>
          <div style={{ minWidth: '180px', padding: '0.5rem' }}>
            <div style={{ fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
              Update Unit {rowUnit} Status
            </div>
            {/* Show "OK", "Resolved" and "Needs Service" as options */}
            {['OK', 'Resolved', 'Needs Service'].map((status) => (
              <button
                type="button"
                className="inspection-status-option"
                key={status}
                onClick={() => handleSelectStatus(row, status)}
                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f2f2f2'; }}
                onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '0.3rem',
                  fontSize: '0.85rem'
                }}
              >
                <span className={`inspection-badge ${getBadgeClass(status)}`}>
                  {status}
                </span>
              </button>
            ))}
          </div>
        </Popover.Body>
      </Popover>
    );
  }

  const ThumbnailSkeleton = ({ text = '' }) => (
    <div
      className="file-thumbnail"
      style={{
        width: '50px',
        height: '50px',
        backgroundColor: '#f0f0f0',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        color: '#999'
      }}
    >
      {text}
    </div>
  );

  // Helper to create video thumbnail
  const VideoThumbnail = ({ src }) => {
    const [hasError, setHasError] = useState(false);

    React.useEffect(() => {
      setHasError(false);
    }, [src]);

    if (hasError) return null;

    return (
      <div className="video-thumbnail">
        <video
          src={src}
          className="file-thumbnail"
          muted
          playsInline
          preload="metadata"
          style={{
            objectFit: 'cover',
            width: '100%',
            height: '100%',
            borderRadius: 4,
            pointerEvents: 'none'
          }}
          onError={() => {
            setValidMedia(prev => ({ ...prev, [src]: false }));
            setHasError(true);
          }}
        />
      </div>
    );
  };

  const renderMediaThumbnail = ({ fileUrl, key, altText, onClick }) => {
    const isHeic = isHeicFile(fileUrl);
    const ThumbnailControl = onClick ? 'button' : 'span';
    const controlProps = onClick ? { type: 'button', className: 'media-thumbnail-button', 'aria-label': `Open ${altText || 'photo'}` } : {};

    if (!shouldLoadImage(fileUrl)) {
      return <ThumbnailSkeleton key={key} />;
    }

    if (isVideoUrl(fileUrl)) {
      return (
        <ThumbnailControl key={key} onClick={onClick} {...controlProps}>
          <VideoThumbnail src={fileUrl} />
        </ThumbnailControl>
      );
    }

    if (isHeic) {
      const nativeFailed = heicNativeFailed[fileUrl] === true;
      const convertedUrl = heicCache[fileUrl];
      const conversionFailed = heicErrors[fileUrl] === true;

      // Native render hasn't been tried yet (or it succeeded): use native URL directly.
      // Safari renders HEIC natively; Chrome/Firefox will fire onError and we'll switch.
      if (!nativeFailed) {
        return (
          <ThumbnailControl key={key} onClick={onClick} {...controlProps}>
            <img
              src={fileUrl}
              alt={altText}
              className="file-thumbnail"
              onLoad={() => markImageLoaded(fileUrl)}
              onError={() => {
                setHeicNativeFailed(prev => ({ ...prev, [fileUrl]: true }));
              }}
            />
          </ThumbnailControl>
        );
      }

      // Native failed. If the converted blob is ready, use it.
      if (convertedUrl) {
        return (
          <ThumbnailControl key={key} onClick={onClick} {...controlProps}>
            <img
              src={convertedUrl}
              alt={altText}
              className="file-thumbnail"
              onLoad={() => markImageLoaded(fileUrl)}
              onError={() => setValidMedia(prev => ({ ...prev, [fileUrl]: false }))}
            />
          </ThumbnailControl>
        );
      }

      // Native failed and conversion is either still running or failed.
      if (!conversionFailed) {
        return <ThumbnailSkeleton key={key} />;
      }

      // Both native render and heic2any conversion failed; mark invalid so the
      // photos column collapses to "No photos" rather than a broken icon.
      // (Defer the state update so React doesn't warn about updating during render.)
      setTimeout(() => {
        setValidMedia(prev => (prev[fileUrl] === false ? prev : { ...prev, [fileUrl]: false }));
      }, 0);
      return null;
    }

    return (
      <ThumbnailControl key={key} onClick={onClick} {...controlProps}>
        <img
          src={fileUrl}
          alt={altText}
          className="file-thumbnail"
          onLoad={() => markImageLoaded(fileUrl)}
          onError={() => setValidMedia(prev => ({ ...prev, [fileUrl]: false }))}
        />
      </ThumbnailControl>
    );
  };

  // Filter out invalid media from carousel
  const filteredCarouselPhotos = projectMode
    ? carouselPhotos
    : carouselPhotos?.filter(url => validMedia[url] !== false);

  const openProjectGallery = (row, stage, index) => {
    const { before, after } = getProjectPhotos(row);
    setProjectCarouselLabels([
      ...before.map(() => 'Before'),
      ...after.map(() => 'After')
    ]);
    handleOpenCarousel([...before, ...after], (stage === 'after' ? before.length : 0) + index);
  };

  const renderProjectPhotos = (row, stage) => {
    const photos = getProjectPhotos(row)[stage];
    const label = stage === 'before' ? 'Before' : 'After';
    if (!photos.length) return <span className="project-empty-photos">No photos</span>;

    return (
      <div className="project-photo-list">
        {photos.slice(0, 3).map((fileUrl, index) => (
          <button
            type="button"
            className="project-photo-button"
            key={`${fileUrl}-${index}`}
            aria-label={`View ${label.toLowerCase()} photo ${index + 1} of ${photos.length} for unit ${row.unit}`}
            onClick={() => openProjectGallery(row, stage, index)}
          >
            {validMedia[fileUrl] === false ? (
              <span className="project-photo-unavailable">Open file</span>
            ) : renderMediaThumbnail({
              fileUrl,
              key: index,
              altText: `${label} work, unit ${row.unit}`
            })}
          </button>
        ))}
        {photos.length > 3 && (
          <button
            type="button"
            className="project-photo-more"
            aria-label={`View all ${photos.length} ${label.toLowerCase()} photos for unit ${row.unit}`}
            onClick={() => openProjectGallery(row, stage, 3)}
          >
            +{photos.length - 3}
          </button>
        )}
      </div>
    );
  };

  // Helper function to build photo-to-issue mapping from rawData (new format only)
  const buildPhotoToIssueMap = (row) => {
    if (!row.rawData || dataFormat !== "new") return {};
    
    const map = {};
    const issueNameMap = {
      "mould": "Potential Mould",
      "drip_pan": "Drain Pan",
      "drip_pan_sensor": "Drain Pan",
      "drip_pan__sensor": "Drain Pan",
      "drain_pan": "Drain Pan",
      "drain_pan_sensor": "Drain Pan",
      "thermostat": "Thermostat",
      "actuator": "Actuator",
      "3_way_valve": "3-Way Valve",
      "field_3_way_valves": "3-Way Valve",
      "n3way_valves": "3-Way Valve",
      "valves": "Valves",
      "hoses": "Hoses",
      "coils": "Coils",
      "compressor": "Compressor",
      "blower_fan": "Blower Fan",
      "bathroom_fan": "Bathroom Fan",
      "booster_fan": "Booster Fan",
      "exhaust_hose": "Exhaust Hose",
      "leak_sensor": "Leak Sensor",
      "dryer": "Dryer",
      "insulation": "Insulation",
      "other": "Other",
      "custom": "Other",
      "custom_issue": "Other",
      "custom issue": "Other",
      "any_other_custom_issues": "Other",
      "anyothercustomissues": "Other",
      "any other custom issues": "Other",
      "anyOtherCustomIssues": "Other"
    };
    
    const getIssueNameFromKey = (key) => {
      const keyLower = key.toLowerCase();
      // Extract issue name from photo field names like "photo_of_actuator_issue" or "photo_of_mould_issue"
      // Also handle "before_photo_of_<issue>" and "after_photo_of_<issue>"
      for (const [fieldKey, displayName] of Object.entries(issueNameMap)) {
        if (keyLower.includes(fieldKey)) {
          return displayName;
        }
      }
      // Try to extract from pattern "photo_of_<issue>_issue" or "before_photo_of_<issue>" or "after_photo_of_<issue>"
      // Handle multi-word issue names like "drain_pan" by matching until "_issue" or end
      let match = keyLower.match(/photo_of_(.+?)_issue/);
      if (!match) {
        match = keyLower.match(/(?:before|after)_photo_of_(.+?)(?:_issue)?$/);
      }
      if (match) {
        const issueKey = match[1];
        // Check if the full key (e.g., "drain_pan") is in the map
        if (issueNameMap[issueKey]) {
          return issueNameMap[issueKey];
        }
        // Also check if any part of the key matches (for backward compatibility)
        for (const [fieldKey, displayName] of Object.entries(issueNameMap)) {
          if (issueKey.includes(fieldKey) || fieldKey.includes(issueKey)) {
            return displayName;
          }
        }
        // Fallback: format the key nicely
        return issueKey.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
      }
      return null;
    };
    
    const getPhotoTypeFromKey = (key) => {
      const keyLower = key.toLowerCase();
      if (keyLower.startsWith("before_photo_of")) return "before";
      if (keyLower.startsWith("after_photo_of")) return "after";
      if (keyLower.startsWith("photo_of")) return "issue";
      return "general";
    };
    
    Object.keys(row.rawData).forEach(key => {
      const keyLower = key.toLowerCase();
      // Skip model sticker plate photos
      if (keyLower.includes("model_sticker") || keyLower.includes("sticker_plate")) {
        return;
      }
      
      if (key.startsWith("photo_of") || key.startsWith("before_photo_of") || key.startsWith("after_photo_of")) {
        const value = row.rawData[key];
        const issueName = getIssueNameFromKey(key);
        const photoType = getPhotoTypeFromKey(key);
        
        if (Array.isArray(value)) {
          value.forEach(url => {
            if (url && typeof url === "string") {
              if (!map[url]) map[url] = { issues: [], type: photoType };
              if (issueName && !map[url].issues.includes(issueName)) {
                map[url].issues.push(issueName);
              }
              map[url].type = photoType; // Store photo type
            }
          });
        } else if (value && typeof value === "string") {
          if (!map[value]) map[value] = { issues: [], type: photoType };
          if (issueName && !map[value].issues.includes(issueName)) {
            map[value].issues.push(issueName);
          }
          map[value].type = photoType; // Store photo type
        }
      } else if (key === "additional_photos_of_the_unit") {
        const value = row.rawData[key];
        if (Array.isArray(value)) {
          value.forEach(url => {
            if (url && typeof url === "string") {
              map[url] = { issues: ["General"], type: "general" };
            }
          });
        } else if (value && typeof value === "string") {
          map[value] = { issues: ["General"], type: "general" };
        }
      }
    });
    
    return map;
  };

  // Build photo-to-issue map when carousel opens (for new format)
  React.useEffect(() => {
    if (!projectMode && showModal && dataFormat === "new" && carouselPhotos && carouselPhotos.length > 0) {
      // Find the row that contains these photos
      const row = pageData.find(r => {
        const isOK = r.inspection === "OK" || r.inspection === "Resolved";
        if (isOK && r.rawData) {
          // For OK status, check before/after photos
          let allPhotos = [];
          Object.keys(r.rawData).forEach(key => {
            const keyLower = key.toLowerCase();
            if (keyLower.includes("model_sticker") || keyLower.includes("sticker_plate")) {
              return;
            }
            if (key.startsWith("before_photo_of") || key.startsWith("after_photo_of")) {
              const value = r.rawData[key];
              if (Array.isArray(value)) {
                allPhotos.push(...value.filter(url => url && typeof url === "string"));
              } else if (value && typeof value === "string") {
                allPhotos.push(value);
              }
            }
          });
          return carouselPhotos.some(photo => allPhotos.includes(photo));
        } else {
          // For Needs Service, check issue photos
          const rowPhotos = r.photos || [];
          return carouselPhotos.some(photo => rowPhotos.includes(photo));
        }
      });
      
      if (row && row.rawData) {
        const map = buildPhotoToIssueMap(row);
        setPhotoToIssueMap(map);
      }
    } else {
      setPhotoToIssueMap({});
    }
  }, [showModal, carouselPhotos, dataFormat, pageData, projectMode]);

  return (
    <div className={`fancoil-table-container${projectMode ? ' project-table-container' : ''}`}>
      <Card className="shadow-sm border-0 fancoil-table-card">
        <Card.Body style={{ padding: 0 }}>
          <div className="maintenance-mobile-sort">
            <label htmlFor="maintenance-mobile-sort">Sort by</label>
            <Form.Select id="maintenance-mobile-sort" value={sortColumn} onChange={event => handleSort(event.target.value)}>
              <option value="unit">Unit</option>
              <option value={dataFormat === 'new' || projectMode ? 'equipmentLocation' : 'inspectionDate'}>{dataFormat === 'new' || projectMode ? 'Location' : 'Inspection date'}</option>
              <option value="notes">Notes</option>
              <option value="inspection">Status</option>
            </Form.Select>
            <button type="button" className="maintenance-sort-direction" onClick={() => handleSort(sortColumn)} aria-label={`Sort ${sortDirection === 'asc' ? 'descending' : 'ascending'}`}>
              {sortDirection === 'asc' ? '↑' : '↓'}
            </button>
          </div>
          <Table role="table" hover responsive className={`fancoil-table mb-0 border-0${projectMode ? ' project-table' : ''}`}>
            <thead role="rowgroup">
              {projectMode ? (
                <tr>
                  <th className="clickable-th"><button type="button" className="project-sort-button" onClick={() => handleSort('unit')}>Unit {getSortArrow('unit')}</button></th>
                  <th className="clickable-th"><button type="button" className="project-sort-button" onClick={() => handleSort('equipmentLocation')}>Location {getSortArrow('equipmentLocation')}</button></th>
                  <th>Before photos</th>
                  <th>After photos</th>
                  <th className="clickable-th notes-column"><button type="button" className="project-sort-button" onClick={() => handleSort('notes')}>Notes {getSortArrow('notes')}</button></th>
                  <th className="clickable-th"><button type="button" className="project-sort-button" onClick={() => handleSort('inspection')}>Status {getSortArrow('inspection')}</button></th>
                </tr>
              ) : <tr>
                <th className="clickable-th"><button type="button" className="project-sort-button" onClick={() => handleSort('unit')}># {getSortArrow('unit')}</button></th>
                <th className="clickable-th"><button type="button" className="project-sort-button" onClick={() => handleSort(dataFormat === "new" ? 'equipmentLocation' : 'inspectionDate')}>
                  {dataFormat === "new" ? "Location" : "Inspection Date"} {getSortArrow(dataFormat === "new" ? 'equipmentLocation' : 'inspectionDate')}
                </button></th>
                <th className="clickable-th" style={dataFormat === "new" ? { maxWidth: '180px', minWidth: '120px' } : {}}><button type="button" className="project-sort-button" onClick={() => handleSort('notes')}>
                  {dataFormat === "new" ? "Issues" : "Notes"} {getSortArrow('notes')}
                </button></th>
                <th>{hvacType === 'dryer-vent' ? (dataFormat === "new" ? 'Photos' : 'Before and After Photo') : 'Photos'}</th>
                {dataFormat === "new" && (
                  <th className="clickable-th notes-column"><button type="button" className="project-sort-button" onClick={() => handleSort('notes')}>Notes {getSortArrow('notes')}</button></th>
                )}
                <th className="clickable-th"><button type="button" className="project-sort-button" onClick={() => handleSort('inspection')}>{hvacHeading} {getSortArrow('inspection')}</button></th>
                <th>Report</th>
              </tr>}
            </thead>
            <tbody role="rowgroup">
              {pageData.length === 0 && <tr className="maintenance-empty-row"><td colSpan={projectMode ? 6 : dataFormat === 'new' ? 7 : 6}>No units match these filters.</td></tr>}
              {pageData.map((row, idx) => {
                const rowId = getRowId(row, idx);
                if (projectMode) {
                  const status = getProjectStatus(row);
                  return (
                    <tr role="row" key={rowId}>
                      <td role="cell" data-label="Unit" className="maintenance-unit-cell">{row.unit}</td>
                      <td role="cell" data-label="Location">{row.equipmentLocation || row.location || '-'}</td>
                      <td role="cell" data-label="Before photos" className="photos-cell">{renderProjectPhotos(row, 'before')}</td>
                      <td role="cell" data-label="After photos" className="photos-cell">{renderProjectPhotos(row, 'after')}</td>
                      <td role="cell" data-label="Notes" className="notes-cell">{getProjectNotes(row) || '-'}</td>
                      <td role="cell" data-label="Status">
                        <span className={`inspection-badge ${status === 'Completed' ? 'badge-ok' : status === 'No Access' ? 'badge-missed' : 'project-badge-pending'}`}>
                          {status}
                        </span>
                      </td>
                    </tr>
                  );
                }
                const finalNotes =
                  row.notes && row.notes.trim() ? row.notes.trim() : 'System Fully Functional';
                const badgeClass = getBadgeClass(row.inspection);
                const isMissed = row.inspection === 'Missed';
                // Only Needs Service / Resolved rows can be re-classified by the user.
                const isUpdatable = row.inspection !== 'OK' && !isMissed;

                // Check for custom issues in rawData when issues array is empty
                const hasCustomIssue = row.rawData && (
                  // Check status field variations
                  row.rawData.any_other_custom_issues === "HAS ISSUES 🔴" ||
                  row.rawData.anyOtherCustomIssues === "HAS ISSUES 🔴" ||
                  (Array.isArray(row.rawData.any_other_custom_issues) && row.rawData.any_other_custom_issues.includes("HAS ISSUES 🔴")) ||
                  (Array.isArray(row.rawData.anyOtherCustomIssues) && row.rawData.anyOtherCustomIssues.includes("HAS ISSUES 🔴")) ||
                  // Check for custom issue photos (indicates custom issue was reported)
                  (row.rawData.photo_of_custom_issue && row.rawData.photo_of_custom_issue.length > 0) ||
                  (row.rawData.anyOtherCustomIssuesPhoto && row.rawData.anyOtherCustomIssuesPhoto.length > 0) ||
                  // Check for custom issue description
                  (row.rawData.description_of_custom_issue && row.rawData.description_of_custom_issue.trim() !== '') ||
                  (row.rawData.anyOtherCustomIssuesDescription && row.rawData.anyOtherCustomIssuesDescription.trim() !== '')
                );
                
                // Fallback: if status is "Needs Service" but no issues found, there must be an issue
                const needsServiceWithoutIssues = row.inspection === "Needs Service" && 
                  (!row.issues || row.issues.length === 0) && !hasCustomIssue;
                
                // Build display issues: use row.issues if available, otherwise check for custom issues or fallback
                const displayIssues = row.issues && row.issues.length > 0 
                  ? row.issues 
                  : (hasCustomIssue || needsServiceWithoutIssues ? ['Other'] : []);

                return (
                  <tr role="row" key={rowId}>
                    <td role="cell" data-label="Unit" className="maintenance-unit-cell">{row.unit}</td>
                    <td role="cell" data-label={dataFormat === "new" ? "Location" : "Inspection date"} style={{ whiteSpace: 'nowrap', padding: '1rem 0.75rem' }}>{dataFormat === "new" ? (row.equipmentLocation || '-') : (row.inspectionDate || '-')}</td>
                    <td role="cell" data-label={dataFormat === "new" ? "Issues" : "Notes"} style={dataFormat === "new" ? { maxWidth:'300px', minWidth: '220px', padding: '1rem 0.75rem' } : {}} className={dataFormat === "new" ? "" : "notes-cell"}>
                      {dataFormat === "new" ? (
                        isMissed ? (
                          <span className="issues-blocked-text">
                            Blocked After 2 Visits
                          </span>
                        ) : displayIssues.length > 0 ? (
                          <span style={{ color: '#d92d20', fontWeight: 600, lineHeight: '1.5' }}>
                            {displayIssues.join(', ')}
                          </span>
                        ) : (
                          <span style={{ color: '#059669', fontWeight: 600 }}>
                            No issues
                          </span>
                        )
                      ) : (
                        // Old format: Show notes
                        finalNotes
                      )}
                    </td>
                    <td role="cell" data-label="Photos" className="photos-cell">
                      {hvacType === 'dryer-vent' ? (
                        dataFormat === "new" ? (
                          // New format: Show before/after photos for OK status, issue photos for Needs Service
                          (() => {
                            const isOK = row.inspection === "OK" || row.inspection === "Resolved";
                            let photosToShow = [];
                            let allPhotos = [];
                            
                            if (isOK && row.rawData) {
                              // For OK status, collect before and after photos
                              const beforePhotos = [];
                              const afterPhotos = [];
                              
                              Object.keys(row.rawData).forEach(key => {
                                const keyLower = key.toLowerCase();
                                if (keyLower.includes("model_sticker") || keyLower.includes("sticker_plate")) {
                                  return;
                                }
                                
                                if (key.startsWith("before_photo_of")) {
                                  const value = row.rawData[key];
                                  if (Array.isArray(value)) {
                                    beforePhotos.push(...value.filter(url => url && typeof url === "string"));
                                  } else if (value && typeof value === "string") {
                                    beforePhotos.push(value);
                                  }
                                } else if (key.startsWith("after_photo_of")) {
                                  const value = row.rawData[key];
                                  if (Array.isArray(value)) {
                                    afterPhotos.push(...value.filter(url => url && typeof url === "string"));
                                  } else if (value && typeof value === "string") {
                                    afterPhotos.push(value);
                                  }
                                }
                              });
                              
                              // Combine before and after photos
                              allPhotos = [...beforePhotos, ...afterPhotos];
                              photosToShow = allPhotos.slice(0, 3);
                            } else {
                              // For Needs Service, show issue photos
                              photosToShow = (row.photos || []).slice(0, 3);
                              allPhotos = row.photos || [];
                            }
                            
                            const maxPhotosToShow = 3; // Show up to 3 photos
                            const photosToDisplay = photosToShow.slice(0, maxPhotosToShow);
                            const remainingCount = allPhotos.length - maxPhotosToShow;
                            
                            return (
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', alignItems: 'center' }}>
                                {photosToDisplay.length > 0 ? (
                                  <>
                                    {photosToDisplay.map((photo, photoIdx) =>
                                      renderMediaThumbnail({
                                        fileUrl: photo,
                                        key: photoIdx,
                                        altText: isOK ? "Before/After" : "Issue",
                                        onClick: () => handleOpenCarousel(allPhotos, photoIdx)
                                      })
                                    )}
                                    {remainingCount > 0 && (
                                      <button type="button" className="project-photo-more"
                                        style={{ 
                                          color: '#666', 
                                          fontSize: '0.85rem', 
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          padding: '4px 8px',
                                          backgroundColor: '#f0f0f0',
                                          borderRadius: '4px'
                                        }}
                                        onClick={() => handleOpenCarousel(allPhotos, maxPhotosToShow)}
                                        title={`View all ${allPhotos.length} photos`}
                                      >
                                        +{remainingCount}
                                      </button>
                                    )}
                                  </>
                                ) : (
                                  <span style={{ color: '#999', fontSize: '0.9rem' }}>No photos</span>
                                )}
                              </div>
                            );
                          })()
                        ) : (
                          // Old format: Show before and after photos
                          (() => {
                            const allOldPhotos = [...(row.beforePhotos || []), ...(row.afterPhotos || [])];
                            const maxPhotosToShow = 3;
                            const photosToDisplay = allOldPhotos.slice(0, maxPhotosToShow);
                            const remainingCount = allOldPhotos.length - maxPhotosToShow;
                            
                            return (
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', alignItems: 'center' }}>
                                {photosToDisplay.length > 0 ? (
                                  <>
                                    {photosToDisplay.map((photo, idx) => {
                                      const isBefore = idx < (row.beforePhotos || []).length;
                                      return renderMediaThumbnail({
                                        fileUrl: photo,
                                        key: idx,
                                        altText: isBefore ? "Before" : "After",
                                        onClick: () => handleOpenCarousel(allOldPhotos, idx)
                                      });
                                    })}
                                    {remainingCount > 0 && (
                                      <button type="button" className="project-photo-more"
                                        style={{ 
                                          color: '#666', 
                                          fontSize: '0.85rem', 
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          padding: '4px 8px',
                                          backgroundColor: '#f0f0f0',
                                          borderRadius: '4px'
                                        }}
                                        onClick={() => handleOpenCarousel(allOldPhotos, maxPhotosToShow)}
                                        title={`View all ${allOldPhotos.length} photos`}
                                      >
                                        +{remainingCount}
                                      </button>
                                    )}
                                  </>
                                ) : (
                                  <span style={{ color: '#999', fontSize: '0.9rem' }}>No photos</span>
                                )}
                              </div>
                            );
                          })()
                        )
                      ) : (
                        (() => {
                          const validPhotos = row.photos ? row.photos.filter(url => validMedia[url] !== false) : [];
                          const maxPhotosToShow = 3; // Show up to 3 photos
                          const photosToDisplay = validPhotos.slice(0, maxPhotosToShow);
                          const remainingCount = validPhotos.length - maxPhotosToShow;
                          
                          return (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', alignItems: 'center' }}>
                              {photosToDisplay.length > 0 ? (
                                <>
                                  {photosToDisplay.map((fileUrl, i) => {
                                      return renderMediaThumbnail({
                                        fileUrl,
                                        key: i,
                                        altText: "Preview",
                                        onClick: () => handleOpenCarousel(validPhotos, i)
                                      });
                                    })}
                                  {remainingCount > 0 && (
                                    <button type="button" className="project-photo-more"
                                      style={{ 
                                        color: '#666', 
                                        fontSize: '0.85rem', 
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        padding: '4px 8px',
                                        backgroundColor: '#f0f0f0',
                                        borderRadius: '4px'
                                      }}
                                      onClick={() => handleOpenCarousel(validPhotos, maxPhotosToShow)}
                                      title={`View all ${validPhotos.length} photos`}
                                    >
                                      +{remainingCount}
                                    </button>
                                  )}
                                </>
                              ) : (
                                <span style={{ color: '#999', fontSize: '0.9rem' }}>No photos</span>
                              )}
                            </div>
                          );
                        })()
                      )}
                    </td>
                    {dataFormat === "new" && (
                      <td role="cell" data-label="Notes" className="notes-cell">
                        {row.notes || 'System Fully Functional'}
                      </td>
                    )}
                    <td role="cell" data-label="Status">
                      {isUpdatable ? (
                        <OverlayTrigger
                          trigger="click"
                          placement="right"
                          show={activeRowId === rowId}
                          overlay={renderStatusPopover(row)}
                          rootClose
                          onToggle={(nextShow) => { setActiveRowId(nextShow ? rowId : null); }}
                        >
                          <button type="button" className={`inspection-badge inspection-status-trigger ${badgeClass}`} aria-label={`Update status for unit ${row.unit}: ${getBadgeLabel(row.inspection)}`} aria-expanded={activeRowId === rowId}>
                            {getBadgeLabel(row.inspection)}
                          </button>
                        </OverlayTrigger>
                      ) : (
                        <span className={`inspection-badge ${badgeClass}`}>
                          {getBadgeLabel(row.inspection)}
                        </span>
                      )}
                    </td>
                    <td role="cell" data-label="Report">
                      <Button size="sm" className="issues-button" onClick={() => onNotifyResident(row)}>
                        View Report
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={projectMode ? 6 : dataFormat === "new" ? 7 : 6}>
                  <div className="fancoil-pagination d-flex justify-content-between align-items-center mt-3">
                    <div className="d-flex align-items-center">
                      <span className="me-2">Rows per page:</span>
                      <Form.Select
                        value={rowsPerPage}
                        onChange={handleRowsPerPageChange}
                        aria-label="Rows per page"
                        className="rows-per-page-select"
                        style={{ width: 'auto' }}
                      >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                      </Form.Select>
                      <span className="ms-3">
                        Showing {sortedData.length === 0 ? 0 : startIndex + 1} to {Math.min(endIndex, sortedData.length)} of {sortedData.length}
                      </span>
                    </div>
                    <div className="d-flex align-items-center" style={{ marginLeft: 'auto' }}>
                      <span className="me-3">Page {currentPage} of {totalPages}</span>
                      <Button variant="outline-secondary" size="sm" className="me-2" onClick={handlePrevPage} disabled={currentPage === 1}>
                        Previous
                      </Button>
                      <Button variant="outline-secondary" size="sm" onClick={handleNextPage} disabled={currentPage === totalPages}>
                        Next
                      </Button>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </Table>
        </Card.Body>
      </Card>

      {/* Modal for photo/video slideshow */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg" className="maintenance-photo-modal" aria-labelledby="maintenance-photo-title">
        <Modal.Header closeButton><Modal.Title id="maintenance-photo-title">{projectMode ? 'Project photos' : 'Inspection photos'}</Modal.Title></Modal.Header>
        <Modal.Body className="p-0" style={{ position: 'relative' }}>
          <Carousel activeIndex={carouselIndex} onSelect={handleSelectCarousel} interval={null}>
            {filteredCarouselPhotos?.map((url, i) => {
              const isActive = i === carouselIndex;
              const photoInfo = photoToIssueMap[url];
              const isVideoFile = isVideoUrl ? isVideoUrl(url) : false;
              const displayUrl = getDisplayUrl(url);
              const isHeic = isHeicFile(url);
              const isReady = !isHeic || !!displayUrl;
              let bannerText = projectMode && isActive ? projectCarouselLabels[i] || 'Project photo' : '';
              
              if (!projectMode && isActive && photoInfo) {
                if (photoInfo.issues && photoInfo.issues.length > 0 && photoInfo.issues[0] !== "General") {
                  const issueName = photoInfo.issues[0];
                  if (photoInfo.type === "before") {
                    bannerText = `Before ${issueName}`;
                  } else if (photoInfo.type === "after") {
                    bannerText = `After ${issueName}`;
                  } else {
                    bannerText = `${isVideoFile ? 'Video' : 'Photo'} of ${issueName} Issue`;
                  }
                }
              }
              
              return (
                <Carousel.Item key={i}>
                  <div style={{ position: 'relative' }}>
                    {bannerText && (
                      <div className="maintenance-photo-caption" style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        backgroundColor: (projectMode || photoInfo?.type === "before" || photoInfo?.type === "after")
                          ? 'rgba(17, 36, 62, 0.9)' 
                          : 'rgba(255, 241, 240, 0.9)',
                        color: (projectMode || photoInfo?.type === "before" || photoInfo?.type === "after")
                          ? 'white' 
                          : '#d92d20',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontWeight: (projectMode || photoInfo?.type === "before" || photoInfo?.type === "after")
                          ? 600 
                          : 'bold',
                        border: (projectMode || photoInfo?.type === "before" || photoInfo?.type === "after")
                          ? 'none' 
                          : '1px solid #d92d20',
                        zIndex: 10,
                        whiteSpace: 'nowrap'
                      }}>
                        {bannerText}
                      </div>
                    )}
                    {projectMode && (validMedia[url] === false || (isHeic && heicNativeFailed[url] && heicErrors[url] && !displayUrl)) ? (
                      <div className="project-media-fallback">
                        <span>This file could not be previewed.</span>
                        <a href={url} target="_blank" rel="noopener noreferrer">Open original file</a>
                      </div>
                    ) : isVideoUrl(url) ? (
                      <video 
                        controls 
                        src={url}
                        className="d-block w-100 carousel-video"
                        onError={() => setValidMedia(prev => ({ ...prev, [url]: false }))}
                      />
                    ) : isReady || (projectMode && isHeic && !heicNativeFailed[url]) ? (
                      <img 
                        src={displayUrl || url}
                        alt={projectMode ? `${projectCarouselLabels[i] || 'Project'} work photo` : 'Preview'}
                        className="d-block w-100 carousel-image"
                        onError={() => {
                          if (projectMode && isHeic && !displayUrl) {
                            setHeicNativeFailed(prev => ({ ...prev, [url]: true }));
                          } else {
                            setValidMedia(prev => ({ ...prev, [url]: false }));
                          }
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          height: '420px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(90deg, #f5f5f5 25%, #ededed 37%, #f5f5f5 63%)',
                          backgroundSize: '400% 100%',
                          animation: 'placeholder-shimmer 1.2s ease-in-out infinite'
                        }}
                      />
                    )}
                  </div>
                </Carousel.Item>
              );
            })}
          </Carousel>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default FancoilTable;
